/**
 * API client configuration and error handling
 *
 * Single API backend:
 *   API_V2_URL — Neon-backed API (port 3002)
 *
 * Auth: cookie sessions only. The session cookie is sent via credentials:'include'.
 * CSRF: X-CSRF-Token header attached on state-changing requests (POST/PUT/PATCH/DELETE).
 *
 * APP-W06a: the API target comes exclusively from the one explicit resolver in
 * ./api-config. API_V2_URL is kept only as a compatible derived export for
 * existing importers — it is never a second fallback. When the build is not
 * authenticating (preview, unconfigured, unapproved release or inactive mode)
 * it is an empty string and credentialed surfaces never mount.
 *
 * APP-W01b:
 * - `apiV2Fetch` returns a structured ApiOutcome instead of throwing; the
 *   unscoped `auth:logout` event dispatch on 401 is REMOVED — identity
 *   reactions are endpoint-aware and owned by the auth provider seam
 *   (`reactToAuthOutcome`), never a generic window event.
 * - 403 permission denial is NEVER a generic logout trigger.
 * - The auto-retry on CSRF_INVALID is REMOVED from the request path and
 *   replaced by the outcome seam `csrfRetryGrant`; the bounded single-replay
 *   consumer stays DISABLED behind the producer pre-handler guarantee
 *   (alignment C01) — generic 403/retryable responses can never enable it.
 */

import { getCsrfToken, isStateChangingMethod } from './csrf';
import { getAuthenticatingApiOrigin } from './api-config';
import { boundedFetch, API_TOTAL_DEADLINE_MS, boundedAuthDeadline } from './transport';

export const API_V2_URL: string = getAuthenticatingApiOrigin() ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public retryable: boolean = false,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Structured outcome codes for the transport-to-identity seam. */
export type ApiOutcomeCode =
  /** Server answered 200 and the body parsed as JSON (or was empty). */
  | 'ok'
  /** Enclosing caller budget exhausted before a usable response. */
  | 'deadline-exceeded'
  /** Caller/identity-generation abort — not a server verdict. */
  | 'caller-aborted'
  /** Unreachable host / DNS / CORS disconnect. */
  | 'network-disconnect'
  /** 401 carrying the exact expired/revoked-session code. */
  | 'session-expired'
  /** 401 without the exact session-expiry signal (ambiguous). */
  | 'auth-ambiguous-401'
  /** 403 permission denial — never a logout trigger. */
  | 'permission-denied'
  /** Any other non-ok status. */
  | 'error';

/** Error status handlers per integration contract (copy/typing only). */
export function handleApiError(status: number, message?: string, code?: string): ApiError {
  switch (status) {
    case 401:
      return new ApiError('Sign-in required. Please authenticate.', status, false, code);
    case 403:
      return new ApiError(message || 'Access denied. You do not have permission.', status, false, code);
    case 429:
      return new ApiError('Too many requests. Please try again later.', status, true, code);
    default:
      if (status >= 500) {
        return new ApiError(message || 'Server error. Please retry.', status, true, code);
      }
      return new ApiError(message || 'An error occurred.', status, false, code);
  }
}

/**
 * Identity-reaction seam: the auth provider installs a handler that applies
 * the structured outcome to the CURRENT identity generation only. The API
 * layer holds no state, no event bus, and no identity authority.
 *
 * W01b-B1: the provider publishes its LIVE generation getter; the api seam
 * reads it ONCE at the admission of each logical request to establish that
 * request's OWNING generation, and attaches that ownership to every auth
 * outcome the request produces. Ownership is never re-read at settlement.
 */
export type AuthOutcomeHandler = (outcome: {
  code: ApiOutcomeCode | 'csrf-invalid';
  status?: number;
  /** Identity-generation snapshot taken at dispatch time. */
  generation?: number;
}) => void;

let authOutcomeHandler: AuthOutcomeHandler | null = null;
/**
 * Provider-published LIVE identity generation getter. Read ONCE per logical
 * request, at admission, to establish ownership; thereafter read only to CHECK
 * whether that ownership is still current. It is never a settlement-time
 * source of identity for an already-running request.
 */
let currentGeneration: () => number | null = () => null;

/**
 * W01b-B1 correction — the ADMISSION-captured owning identity generation.
 *
 * Auth consequences belong to the identity generation that OWNED the logical
 * request when it was dispatched/admitted — never to whichever identity
 * happens to be current when the response settles. This helper is the ONE
 * place ownership is sampled: called at the START of a logical API operation
 * (before any wire I/O) and never again for that operation, so the value is
 * captured once, immutable for the logical request and propagated explicitly.
 *
 * Sampling the live getter at settlement time (the previous behaviour) is
 * forbidden here: it manufactures ownership for an already-running request
 * and makes a stale request look current.
 */
function captureAdmissionGeneration(): number | null {
  return currentGeneration();
}

/** Currency CHECK only (never ownership manufacture): is `generation` still live? */
function isAdmissionGenerationCurrent(generation: number | null): boolean {
  if (generation == null) return false;
  return currentGeneration() === generation;
}

/**
 * W01b-B2 — the ONE raw ambiguous-401 identity probe seam. The provider
 * installs the real probe (bounded, generation-fenced); api.ts never
 * decides identity by itself. At most ONE probe runs per ambiguous 401 and
 * it is re-armed only by a NEW ambiguous outcome.
 *
 * W01b-B1: the probe receives the OWNING generation of the logical request
 * that produced the ambiguous 401 (captured at that request's admission,
 * never re-sampled). The provider refuses to apply a result that no longer
 * belongs to the live generation.
 */
export type AmbiguousIdentityProbe = (
  ownerGeneration: number | null,
  remainingMs: number
) => Promise<boolean>;

let ambiguousIdentityProbe: AmbiguousIdentityProbe | null = null;
/** true while the single allowed probe for the current ambiguous outcome is running/settled. */
let ambiguousProbeSpent = false;

export function setAmbiguousIdentityProbe(probe: AmbiguousIdentityProbe | null): void {
  ambiguousIdentityProbe = probe;
  ambiguousProbeSpent = false;
}

async function requestAmbiguousIdentityProbe(ownerGeneration: number | null): Promise<boolean> {
  if (ambiguousProbeSpent || !ambiguousIdentityProbe) return false;
  ambiguousProbeSpent = true; // maximum ONE probe per ambiguous outcome — no loop
  try {
    return await ambiguousIdentityProbe(ownerGeneration, API_TOTAL_DEADLINE_MS);
  } catch {
    return false;
  }
}

export function setAuthOutcomeHandler(handler: AuthOutcomeHandler | null): void {
  authOutcomeHandler = handler;
}

/**
 * The provider publishes its generation snapshot; every reaction carries it
 * so the handler can refuse stale-generation sign-outs.
 */
export function setIdentityGenerationSource(source: () => number | null): void {
  currentGeneration = source;
}

/**
 * Report an auth outcome with an EXPLICIT owning generation.
 *
 * The caller passes the generation captured for its logical request at
 * dispatch/admission time (never a settlement-time re-read of the live
 * getter). `null` means no identity was confirmed when the request was
 * admitted, which is a valid ownership value: the outcome belongs to nobody
 * and can never authorise a collapse.
 */
function reportAuthOutcome(
  code: ApiOutcomeCode | 'csrf-invalid',
  ownerGeneration: number | null,
  status?: number
): void {
  if (authOutcomeHandler) {
    authOutcomeHandler({
      code,
      status,
      ...(ownerGeneration != null ? { generation: ownerGeneration } : {}),
    });
  }
}

/** Successful result envelope: the parsed response body. */
export type ApiSuccess<T> = { kind: 'success'; data: T };
/** The bounded single-replay seam. Disabled until C01 is accepted. */
export const CSRF_REPLAY_ENABLED = false;

/**
 * Explicit producer pre-handler guarantee switch. Stays false until the C01
 * accepted receipt authorises the exact pre-handler ordering; never flipped
 * from generic 403/retryable results at runtime.
 */
export function csrfReplayProducerGuaranteeAccepted(): boolean {
  return false;
}

export type ApiOutcome<T> =
  | ApiSuccess<T>
  | { kind: 'error'; error: ApiError; code: ApiOutcomeCode }
  | {
      kind: 'csrf-invalid';
      /** Bounded replay material, present only when the seam is enabled. */
      replay: null | {
        endpoint: string;
        method: string;
        headers: Record<string, string>;
        bodyText: string | null;
        csrfToken: string | null;
        /** The ORIGINAL (still enclosing) deadline for the replay. */
        deadlineMs: number;
        /**
         * The identity generation that OWNED the logical request when it was
         * admitted. A replay belonging to the same logical request retains
         * this ownership; it never re-samples the live generation.
         */
        owningGeneration: number | null;
      };
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Legacy-compatible wrapper for existing hook consumers (queries.ts,
 * scout-queries.ts and their test mocks): identical transport semantics to
 * apiV2Fetch plus the W01b-B2 ambiguous-401 single-probe path (the probe is
 * provider-installed, generation-fenced and bounded). Unpacks the outcome
 * to the historical throw-on-failure signature so no existing caller
 * behaviour changes. The CSRF-reject case throws the same 403 ApiError the
 * previous auto-retry path eventually produced, minus the silent refetch
 * loop; a generic 403 performs ZERO probe and ZERO identity reaction.
 */
export async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit & { deadlineRemainingMs?: number }
): Promise<T> {
  const result = await apiV2FetchWithProbe<T>(endpoint, options);
  switch (result.kind) {
    case 'success':
      return result.data;
    case 'error':
      throw result.error;
    case 'csrf-invalid':
      throw new ApiError('Session expired, please refresh or log in again.', 403, false, 'CSRF_INVALID');
  }
}

/**
 * Fetch wrapper for the Neon-backed API — structured outcomes.
 *
 * One logical 30-second budget covers framing AND body. The auth/CSRF
 * ceiling stays bounded inside the remaining budget by the transport.
 * On a state-changing CSRF rejection — ONLY when the producer pre-handler
 * guarantee is accepted — the caller may invoke the bounded single replay
 * with the exact captured request/token/input and the ORIGINAL deadline;
 * timeout/429/5xx never mutate-retry. The replay consumer is built but
 * DISABLED (CSRF_REPLAY_ENABLED false) until alignment C01 lands.
 */
export async function apiV2Fetch<T>(
  endpoint: string,
  options?: RequestInit & { deadlineRemainingMs?: number }
): Promise<ApiOutcome<T>> {
  // W01b-B1: ownership is captured ONCE, here, at the START of the logical
  // operation — BEFORE any wire I/O. Every auth result this request produces
  // carries this value; it is never re-sampled at settlement.
  return runApiFetch<T>(endpoint, options, captureAdmissionGeneration());
}

/**
 * The logical request body. `ownerGeneration` is the identity generation
 * captured at admission by the public entry points (`apiV2Fetch`,
 * `apiV2FetchWithProbe`) — this function never samples the live getter for
 * ownership, so a request that started under A keeps A ownership end to end.
 */
async function runApiFetch<T>(
  endpoint: string,
  options: (RequestInit & { deadlineRemainingMs?: number }) | undefined,
  ownerGeneration: number | null
): Promise<ApiOutcome<T>> {
  const material = captureRequestMaterial(endpoint, options);
  const totalMs = Math.min(
    API_TOTAL_DEADLINE_MS,
    Math.max(0, options?.deadlineRemainingMs ?? API_TOTAL_DEADLINE_MS)
  );
  const outcome = await executeApiFetch<T>(material, options, totalMs, ownerGeneration);

  // Bounded single-replay seam: CSRF-specific 403 ONLY; the exact captured
  // request/token/input and the ORIGINAL deadline are offered at most once
  // and ONLY while the producer pre-handler guarantee is accepted. Timeout,
  // 429, 5xx and generic 403 never produce replay material. A replay that is
  // part of the SAME logical request retains the ORIGINAL request ownership
  // (`owningGeneration`) — it can never acquire a newer identity merely
  // because the current generation changed between attempts.
  if (outcome.code === 'csrf-invalid') {
    const replayEnabled =
      CSRF_REPLAY_ENABLED && csrfReplayProducerGuaranteeAccepted();
    return {
      kind: 'csrf-invalid',
      replay: replayEnabled
        ? {
            endpoint: material.endpoint,
            method: material.method,
            headers: material.headers,
            bodyText: material.bodyText,
            csrfToken: material.csrfToken,
            deadlineMs: totalMs,
            owningGeneration: ownerGeneration,
          }
        : null,
    };
  }
  if (outcome.code !== 'ok') {
    const error =
      outcome.code === 'deadline-exceeded'
        ? new ApiError('Request timed out. Please try again.', 408, true)
        : outcome.code === 'network-disconnect'
          ? new ApiError('Network error. Please check your connection.', 0, true)
          : outcome.code === 'caller-aborted'
            ? new ApiError('Request cancelled.', 0, false)
            : handleApiError(outcome.status ?? 0, undefined, undefined);
    // W01b-B2: a generic 403 performs ZERO identity reaction and ZERO
    // probe. Only the ambiguous-401 case requests the bounded single probe.
    return { kind: 'error', error, code: outcome.code };
  }
  return { kind: 'success', data: outcome.data };
}

/**
 * W01b-B2: the caller-side ambiguous-401 probe path. Classification stays
 * `auth-ambiguous-401` (permission-denied / csrf-invalid NEVER probe), and
 * when the provider-installed probe establishes the session is genuinely
 * absent, the established evidence flows through the normal structured
 * outcome seam (session-expired) carrying the ORIGINAL request ownership.
 * At most one probe runs per ambiguous outcome.
 *
 * W01b-B1: the probe is a SENSITIVE FOLLOW-UP of the same logical request.
 * Its ownership is the generation captured when the request was admitted —
 * never the identity that happens to be current once the ambiguous 401 has
 * settled. If that owning generation is no longer current, the follow-up is
 * REFUSED outright: the probe is not run on behalf of the newer identity.
 */
export async function apiV2FetchWithProbe<T>(
  endpoint: string,
  options?: RequestInit & { deadlineRemainingMs?: number }
): Promise<ApiOutcome<T>> {
  const ownerGeneration = captureAdmissionGeneration();
  const result = await runApiFetch<T>(endpoint, options, ownerGeneration);
  if (result.kind === 'error' && result.code === 'auth-ambiguous-401') {
    // W01b-B2: the probe seam exists ONLY when the provider (or test) has
    // mounted it. Without an installed probe there is no evidence path —
    // the ambiguous 401 stays local (never auto-logout without evidence).
    if (!ambiguousIdentityProbe) {
      return result;
    }
    // W01b-B1 stale-follow-up fence: the owning identity has departed, so
    // this ambiguous 401 belongs to a generation that no longer owns the
    // session. Refuse the probe rather than interrogating the server on
    // behalf of the new identity.
    if (!isAdmissionGenerationCurrent(ownerGeneration)) {
      return result;
    }
    const sessionPresent = await requestAmbiguousIdentityProbe(ownerGeneration);
    if (!sessionPresent) {
      // Probe-established absence: exact session-expiry evidence, still
      // carrying the ORIGINAL owning generation (the provider's handler
      // fences it against the live generation).
      reportIdentityProbeOutcome('session-expired', ownerGeneration, 401);
      return {
        kind: 'error',
        error: handleApiError(401, undefined, undefined),
        code: 'session-expired',
      };
    }
    // Probe-established presence: no identity action — the ambiguous 401
    // was transient (e.g. legacy wrong-password shape); stays local.
  }
  return result;
}

/** Structured outcome report from the probe path (same handler, explicit ownership). */
function reportIdentityProbeOutcome(
  code: ApiOutcomeCode,
  ownerGeneration: number | null,
  status?: number
): void {
  reportAuthOutcome(code, ownerGeneration, status);
}

interface CapturedRequest {
  endpoint: string;
  headers: Record<string, string>;
  method: string;
  bodyText: string | null;
  /** The CSRF token attached to the captured request, if any. */
  csrfToken: string | null;
}

function captureRequestMaterial(
  endpoint: string,
  options?: RequestInit & { deadlineRemainingMs?: number }
): CapturedRequest {
  const headers: Record<string, string> = {};
  if (options?.body != null) {
    headers['Content-Type'] = 'application/json';
  }
  const method = (options?.method ?? 'GET').toUpperCase();
  let csrfToken: string | null = null;
  if (isStateChangingMethod(method)) {
    csrfToken = getCsrfToken();
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  }
  if (options?.headers) {
    Object.assign(headers, options.headers as Record<string, string>);
  }
  return {
    endpoint,
    headers,
    method,
    bodyText: typeof options?.body === 'string' ? options.body : null,
    csrfToken,
  };
}

type ExecutedApiOutcome<T> =
  | { code: 'ok'; data: T }
  | { code: 'session-expired'; status: 401 }
  | { code: 'auth-ambiguous-401'; status: 401 }
  | { code: 'permission-denied'; status: 403 }
  | { code: 'csrf-invalid'; status: 403 }
  | { code: 'deadline-exceeded' | 'network-disconnect'; status?: undefined }
  | { code: 'caller-aborted'; status?: undefined }
  | { code: 'error'; status: number };

/**
 * The wire execution for one logical request. `ownerGeneration` is the
 * generation captured by the entry point at admission; it is attached to
 * EVERY auth outcome this request reports and is never re-sampled here.
 */
async function executeApiFetch<T>(
  material: CapturedRequest,
  options: (RequestInit & { deadlineRemainingMs?: number }) | undefined,
  totalMs: number,
  ownerGeneration: number | null
): Promise<ExecutedApiOutcome<T>> {
  const endpointPath = material.endpoint.split('?')[0] ?? material.endpoint;
  const timeoutMs = endpointPath.startsWith('/auth/') ? boundedAuthDeadline(totalMs) : totalMs;

  const outcome = await boundedFetch(`${API_V2_URL}${material.endpoint}`, {
    method: material.method,
    headers: material.headers,
    body: material.bodyText,
    ...(options?.signal ? { signal: options.signal } : {}),
    deadlineMs: timeoutMs,
  });

  if (outcome.kind === 'timeout') {
    reportAuthOutcome('deadline-exceeded', ownerGeneration);
    return { code: 'deadline-exceeded' };
  }
  if (outcome.kind === 'network-error') {
    reportAuthOutcome('network-disconnect', ownerGeneration);
    return { code: 'network-disconnect' };
  }
  if (outcome.kind === 'aborted') {
    // Caller/identity abort — never translated into an identity verdict.
    const code: ApiOutcomeCode = 'caller-aborted';
    reportAuthOutcome(code, ownerGeneration);
    return { code };
  }

  const { status, bodyText } = outcome;

  // Session-expired code contract: an exact match carries the identity
  // reaction; an ambiguous 401 (wrong-password legacy shape, unusual body)
  // stays local. Permission denial (403 without the CSRF code) never reacts.
  if (status === 401) {
    let body: unknown = null;
    try {
      body = bodyText.length > 0 ? JSON.parse(bodyText) : null;
    } catch {
      body = null;
    }
    const errorCode = isRecord(body) && typeof body.error === 'string' ? body.error : null;
    if (errorCode === 'SESSION_REQUIRED' || errorCode === 'WORKSPACE_ACCESS_REVOKED') {
      reportAuthOutcome('session-expired', ownerGeneration, status);
      return { code: 'session-expired', status };
    }
    reportAuthOutcome('auth-ambiguous-401', ownerGeneration, status);
    return { code: 'auth-ambiguous-401', status };
  }

  if (status === 403) {
    let body: unknown = null;
    try {
      body = bodyText.length > 0 ? JSON.parse(bodyText) : null;
    } catch {
      body = null;
    }
    const errorCode = isRecord(body) && typeof body.error === 'string' ? body.error : null;
    if (errorCode === 'CSRF_INVALID') {
      return { code: 'csrf-invalid', status };
    }
    reportAuthOutcome('permission-denied', ownerGeneration, status);
    return { code: 'permission-denied', status };
  }

  if (!outcome.ok || status >= 400) {
    reportAuthOutcome('error', ownerGeneration, status);
    return { code: 'error', status };
  }

  if (bodyText.length === 0) return { code: 'ok', data: {} as T };
  try {
    return { code: 'ok', data: JSON.parse(bodyText) as T };
  } catch {
    return { code: 'error', status: 502 };
  }
}

/**
 * One-attempt bounded raw identity probe for the ambiguous-legacy-401 seam
 * (W01b): a single /auth/me probe through the raw transport (10-second total
 * deadline, bounded INSIDE the remaining enclosing budget). Callers must
 * hold the current-generation check (auth provider does this); api.ts never
 * decides identity by itself and never probes on its own initiative.
 */
export async function probeAmbiguousAuthIdentity(remainingMs: number): Promise<boolean> {
  const origin = getAuthenticatingApiOrigin();
  if (!origin) return false;
  const outcome = await boundedFetch(`${origin}/auth/me`, {
    deadlineMs: boundedAuthDeadline(remainingMs),
  });
  if (outcome.kind !== 'response') return false;
  if (outcome.status !== 200 || !outcome.ok) return false;
  try {
    JSON.parse(outcome.bodyText);
  } catch {
    return false;
  }
  return true;
}
